import { Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PatientSubscription } from "src/api/types";
import { usePatientLocale } from "src/hooks/usePatientLocale";
import WhiteCard from "src/shared/components/WhiteCard";

interface CardProps {
  subscription: PatientSubscription;
}

const mapMonthsToWeeks: Record<number, string> = {
  1: "4",
  3: "12",
  5: "20",
  6: "24",
};

const PlanCard = ({ subscription }: CardProps) => {
  const { t } = useTranslation();
  const component = "subscription-details.planCard";
  const locale = usePatientLocale();

  const duration = useMemo(
    () => subscription?.price.number_of_months,
    [subscription?.price.number_of_months],
  );

  const amount = useMemo(
    () => subscription?.price.amount,
    [subscription?.price.amount],
  );

  const canceled = useMemo(() => {
    if (subscription == null) return false;

    return (
      subscription.status === "canceled" ||
      subscription.status === "scheduled_for_cancellation"
    );
  }, [subscription]);

  const renewalDate = useMemo(() => {
    if (canceled) return null;
    if (subscription?.current_period_end == null) return null;

    const dateFormat =
      locale === enUS ? "MMMM do, yyyy" : "dd 'de' MMMM 'de' yyyy";

    return format(new Date(subscription.current_period_end), dateFormat, {
      locale,
    });
  }, [canceled, locale, subscription.current_period_end]);

  return (
    <WhiteCard gap={2} mt={3}>
      <Stack gap={1}>
        <Typography variant="h6" fontWeight={600}>
          {t(`${component}.title`, "Subscription")}
        </Typography>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">
            {t(`${component}.subheader1`, "Plan")}
          </Typography>
          <Typography>
            {t(`${component}.planDuration`, "{{weekNum}} weeks plan", {
              weekNum: mapMonthsToWeeks[duration],
            })}
          </Typography>
          <Typography variant="subtitle2">
            {t(
              `${component}.chargeInterval`,
              "Charged ${{amount}} every {{weekNum}} weeks",
              {
                amount: amount,
                weekNum: mapMonthsToWeeks[duration],
              },
            )}
          </Typography>
          <Typography variant="subtitle2">
            {t(`${component}.autoRenew`, "Auto-renews {{renewalDate}}", {
              renewalDate,
            })}
          </Typography>
        </Stack>
      </Stack>
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">
          {t(`${component}.treatment`, "Treatment")}
        </Typography>
        <Typography>
          {t(
            `medications.${subscription.product.display_name?.toLowerCase()}.capitalized`,
            subscription?.product.name || "",
          )}
        </Typography>
      </Stack>
      {renewalDate ? (
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">
            {t(`${component}.activeUntil`, "RX active until")}
          </Typography>
          <Typography>{renewalDate}</Typography>
        </Stack>
      ) : null}
    </WhiteCard>
  );
};

export default PlanCard;
